// web/src/features/home/tempUnit.ts
// The temperature unit of the Home greeting bar and the Weather tile: one
// per-user preference, chosen in Customize and saved with the user's own
// layout (prefs.tempUnit, /api/v1/home/layout). /api/v1/weather (MET Norway)
// always answers in Celsius; the client converts.
import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { homeLayoutQuery } from './useHomeLayout';
import type { HomeLayout, HomePrefs, TempUnit } from './types';

// What the greeting bar and a new Weather tile have always shown.
export const DEFAULT_TEMP_UNIT: TempUnit = 'f';

export const isTempUnit = (v: unknown): v is TempUnit => v === 'f' || v === 'c';

// The saved preference; before the user picks one, the unit their Weather tile
// was set to (the tile carried its own unit before this preference existed),
// else the default.
export function resolveTempUnit(layout: Pick<HomeLayout, 'widgets' | 'prefs'> | null | undefined): TempUnit {
  const pref = layout?.prefs?.tempUnit;
  if (isTempUnit(pref)) return pref;
  const tile = layout?.widgets?.find((w) => w.type === 'weather' && isTempUnit(w.config?.unit));
  return tile ? (tile.config.unit as TempUnit) : DEFAULT_TEMP_UNIT;
}

// The prefs to save with the layout once `unit` was chosen in Customize. Stored
// only when it differs from what the user gets anyway, so an untouched default
// stays a default.
export function prefsWithTempUnit(layout: Pick<HomeLayout, 'widgets' | 'prefs'>, unit: TempUnit | null): HomePrefs | undefined {
  if (!unit || unit === resolveTempUnit(layout)) return layout.prefs;
  return { ...layout.prefs, tempUnit: unit };
}

export const toFahrenheit = (c: number) => (c * 9) / 5 + 32;

// "47°F" / "8°C" from a Celsius reading (rounded after converting); none is a dash.
export function formatTemp(celsius: number | null | undefined, unit: TempUnit): string {
  if (celsius == null || !Number.isFinite(celsius)) return '—';
  return `${Math.round(unit === 'f' ? toFahrenheit(celsius) : celsius)}°${unit === 'f' ? 'F' : 'C'}`;
}

// The unit being chosen in Customize; null outside it, and while an admin
// edits a role's layout (a role carries no personal preference). The greeting
// bar and every Weather tile follow it at once, before Save; HomePage saves it
// with the layout and drops it on Cancel.
type DraftState = { unit: TempUnit | null; setUnit: (unit: TempUnit | null) => void };
export const useTempUnitDraft = create<DraftState>((set) => ({
  unit: null,
  setUnit: (unit) => set({ unit }),
}));

// The unit to show now: the Customize draft, else the saved preference.
// Undefined while the layout is still loading, so a temperature waits for it
// instead of flashing the other unit. A reader never refetches the layout on
// its own (staleTime Infinity); HomePage's query keeps it fresh.
export function useTempUnit(): TempUnit | undefined {
  const draft = useTempUnitDraft((s) => s.unit);
  const q = useQuery({ ...homeLayoutQuery, staleTime: Infinity });
  if (draft) return draft;
  if (q.data) return resolveTempUnit(q.data.layout);
  return q.isError ? DEFAULT_TEMP_UNIT : undefined;
}
